import { useState, type FormEvent, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import {
  adminApi,
  type DeliveryStatus,
  type NotificationOptOut,
  type NotificationSummary,
  type OptOutSource,
  type SendCategory,
  type SendStatus,
} from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import { useCan } from '../lib/capabilities.js';
import { count, numeric } from '../lib/format.js';
import {
  BOUNCE_RATE_WARN,
  COMPLAINT_RATE_WARN,
  MIN_SENDS_FOR_RATE,
  STUCK_QUEUE_SECONDS,
  deliveryLabel,
  isNotFound,
  percent,
  queueAge,
  rateFlags,
  resolveSection,
  sendsPath,
  sourceLabel,
  statusLabel,
  visibleSections,
  type NotificationSection,
} from '../lib/notifications.js';
import { TabNav, type RecordTabs } from '../components/Record.js';
import { FilterChip, FilterPills, InfoBubble, Menu, MenuItem, Screen, inputStyle } from '../components/Screen.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Note,
  Panel,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { DeliveryText, StatusPill } from '../components/NotificationStatus.js';
import { SendDetail } from './SendDetail.js';
import { GroupEmailCompose, GroupEmailList } from './GroupEmail.js';

/**
 * Every email Ridgewill sends, and the people who asked not to get them
 * (org-admin#131, over RidgeWill/auth#508's read routes).
 *
 * ── Three tabs, each behind its own permission ───────────────────────
 *
 * Overview and Sends need `notification:read`; Opt-outs needs `optOut:read`,
 * which is separate because that list is the CAN-SPAM register and plain-text
 * addresses. So a Support Admin sees two tabs, a Legal Admin one, and a role
 * with neither does not see the screen in the navigation at all.
 *
 * Hiding a TAB is new for this portal, which elsewhere only disables buttons.
 * A disabled tab would be a door to a list the role can never read, and the
 * server refuses it anyway. The capability answer stays optimistic while
 * unknown, so a failed fetch shows every tab and lets the server say no.
 *
 * ── No retrying count, on purpose ────────────────────────────────────
 *
 * #131 asked for queued, sending and retrying. auth stores no retrying status:
 * a retry goes back to `queued` with a later `nextAttemptAt`. Jon took the
 * mock-up without it (2026-09-16), so the queue counts a retry as queued and a
 * send's own detail shows its attempts.
 */
export function Notifications({
  section,
  id,
  address,
  navigate,
}: {
  section?: string;
  id?: string;
  address?: string;
  navigate: (to: string) => void;
}) {
  const notificationRead = useCan('notification:read');
  const optOutRead = useCan('optOut:read');
  const [first, ...rest] = visibleSections({ notificationRead, optOutRead });

  if (!first) {
    return (
      <Screen>
        <Note>
          Your role does not include Notifications. Reading sends needs the notification read
          permission, and the opt-out list needs its own.
        </Note>
      </Screen>
    );
  }

  const tabs: RecordTabs<NotificationSection> = [first, ...rest];
  const current = resolveSection(tabs, section) ?? first.path;

  if (current === 'sends') {
    return <Sends tabs={tabs} sendId={id} address={address} navigate={navigate} />;
  }
  if (current === 'group-email') {
    return id ? (
      <GroupEmailCompose key={id} tabs={tabs} id={id} navigate={navigate} />
    ) : (
      <GroupEmailList tabs={tabs} navigate={navigate} />
    );
  }
  if (current === 'opt-outs') return <OptOuts tabs={tabs} />;
  return <Overview tabs={tabs} />;
}

function Tabs({ tabs, current }: { tabs: RecordTabs<NotificationSection>; current: NotificationSection }) {
  return <TabNav base="/notifications" tabs={tabs} current={current} label="Notifications sections" />;
}

/**
 * A 404 from these routes is a missing permission, an auth service older than
 * #508, or (on one send) an id that does not exist. The portal cannot tell them
 * apart, so it names them rather than repeating `describe()`'s sentence about
 * the admin role as a whole, which is wrong on a screen that has plainly loaded.
 */
function Refused({ what }: { what: string }) {
  return (
    <Note>
      {what} could not be read. Either your role does not allow it, or the auth service is older
      than the Notifications admin routes.
    </Note>
  );
}

function Failure({ message, what }: { message: string; what: string }) {
  return isNotFound(message) ? <Refused what={what} /> : <Note tone="danger">{message}</Note>;
}

// ── Overview ─────────────────────────────────────────────────────────

function Overview({ tabs }: { tabs: RecordTabs<NotificationSection> }) {
  const { state, reload } = useAsync(() => adminApi.notificationSummary(), []);

  return (
    <Screen actions={<Button onClick={() => reload()}>Refresh</Button>}>
      <Tabs tabs={tabs} current="overview" />
      {state.status === 'failed' ? (
        <Failure message={state.message} what="The summary" />
      ) : (
        <AsyncBoundary state={state}>{(d) => <Summary summary={d} />}</AsyncBoundary>
      )}
    </Screen>
  );
}

function Summary({ summary }: { summary: NotificationSummary }) {
  const { queue } = summary;
  const stuck = (queue.oldestQueuedAgeSeconds ?? 0) > STUCK_QUEUE_SECONDS;

  return (
    <>
      <Panel
        title="Queue"
        info={
          <InfoBubble label="What the queue counts">
            Messages waiting to go and messages going now. A send the provider refused goes back to
            the queue for another try, so a retry counts as queued. Open a send to see its attempts.
          </InfoBubble>
        }
      >
        <div style={s.tiles}>
          <Tile label="Queued" value={count(queue.queued)} />
          <Tile label="Sending" value={count(queue.sending)} />
          <Tile
            label="Oldest queued"
            value={queueAge(queue.oldestQueuedAgeSeconds)}
            note={stuck ? 'Longer than any retry waits. The queue may be stuck.' : undefined}
            warn={stuck}
          />
        </div>
      </Panel>

      <Panel title="Outcomes">
        <Table columns={['', 'Sent', 'Suppressed', 'Refused', 'Failed']}>
          <OutcomeRow label="Last 24 hours" counts={summary.last24h} />
          <OutcomeRow label="Last 7 days" counts={summary.last7d} />
        </Table>
      </Panel>

      <Panel
        title="Deliverability by app, last 7 days"
        info={
          <InfoBubble label="When a rate is highlighted">
            A bounce rate above {percent(BOUNCE_RATE_WARN)} or a complaint rate above{' '}
            {percent(COMPLAINT_RATE_WARN)} is highlighted, once an app has sent at least{' '}
            {MIN_SENDS_FOR_RATE}. Google and Yahoo ask bulk senders to keep complaints under 0.1%,
            and a bounce rate over 2% usually means a stale address list.
          </InfoBubble>
        }
      >
        {summary.deliverability.length === 0 ? (
          <EmptyState
            title="No email sent in the last 7 days"
            suggestion="Rates appear here once an app has sent something."
          />
        ) : (
          <Table columns={['App', 'Sent', 'Bounced', 'Bounce rate', 'Complaints', 'Complaint rate']}>
            {summary.deliverability.map((row) => {
              const flags = rateFlags(row);
              return (
                <tr key={row.app}>
                  <Td>
                    <code>{row.app}</code>
                  </Td>
                  <Td>
                    <span style={numeric}>{count(row.sent)}</span>
                  </Td>
                  <Td>
                    <span style={numeric}>{count(row.bounced)}</span>
                  </Td>
                  <Td>
                    <Rate value={row.bounceRate} flagged={flags.bounce} />
                  </Td>
                  <Td>
                    <span style={numeric}>{count(row.complained)}</span>
                  </Td>
                  <Td>
                    <Rate value={row.complaintRate} flagged={flags.complaint} />
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Panel>
    </>
  );
}

function OutcomeRow({ label, counts }: { label: string; counts: NotificationSummary['last24h'] }) {
  return (
    <tr>
      <Td>
        <span style={{ color: C.muted }}>{label}</span>
      </Td>
      {[counts.sent, counts.suppressed, counts.refused, counts.failed].map((n, i) => (
        <Td key={i}>
          <span style={numeric}>{count(n)}</span>
        </Td>
      ))}
    </tr>
  );
}

function Rate({ value, flagged }: { value: number; flagged: boolean }) {
  return flagged ? (
    <Badge tone="warn">{percent(value)}</Badge>
  ) : (
    <span style={numeric}>{percent(value)}</span>
  );
}

function Tile({ label, value, note, warn }: { label: string; value: string; note?: string; warn?: boolean }) {
  return (
    <div style={{ ...s.tile, ...(warn ? { borderColor: C.warnDot } : {}) }}>
      <span style={s.tileLabel}>{label}</span>
      <span style={{ ...s.tileValue, ...numeric, color: warn ? C.warnText : C.text }}>{value}</span>
      {note ? <span style={s.tileNote}>{note}</span> : null}
    </div>
  );
}

// ── Sends ────────────────────────────────────────────────────────────

const PAGE = 50;

const WINDOWS = [
  { value: '24', label: '24 hours' },
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' },
  { value: '0', label: 'All time' },
] as const;

const STATUSES: SendStatus[] = ['sent', 'queued', 'sending', 'suppressed', 'refused', 'failed'];
const DELIVERIES: DeliveryStatus[] = ['delivered', 'delayed', 'bounced', 'complained', 'failed', 'suppressed'];
const CATEGORIES: SendCategory[] = ['security', 'account', 'invitation', 'reminder', 'announcement'];

/**
 * The send log, newest first.
 *
 * ── App and type filter by click, like Activity ──────────────────────
 *
 * A dropdown of apps or types would need a list this portal cannot get without
 * carrying a copy that drifts. Status, delivery and category ARE dropdowns,
 * because auth's route validates them against fixed lists and refuses anything
 * else, so those lists are part of the contract rather than a guess.
 *
 * ── Cursor pages, not offsets ────────────────────────────────────────
 *
 * The route pages by `(createdAt, id)` and never counts. Previous keeps a stack
 * of the cursors that led here, so paging back is exact rather than refetching
 * from the start. There is no "12 of 340" line because there is no total.
 *
 * ── The list outlives the drill-in ───────────────────────────────────
 *
 * Opening a send swaps the BODY and keeps these hooks mounted, the Activity
 * screen's pattern, so coming back keeps the filters and the page.
 */
function Sends({
  tabs,
  sendId,
  address: initialAddress,
  navigate,
}: {
  tabs: RecordTabs<NotificationSection>;
  sendId?: string;
  address?: string;
  navigate: (to: string) => void;
}) {
  const [windowHours, setWindowHours] = useState<string>('168');
  const [from, setFrom] = useState<string | undefined>(() => isoAgo(168));
  const [status, setStatus] = useState<SendStatus | ''>('');
  const [delivery, setDelivery] = useState<DeliveryStatus | ''>('');
  const [category, setCategory] = useState<SendCategory | ''>('');
  const [app, setApp] = useState<string>();
  const [type, setType] = useState<string>();
  const [address, setAddress] = useState<string | undefined>(initialAddress);
  const [cursors, setCursors] = useState<string[]>([]);

  /** Any filter change starts again from the newest page. */
  const refilter = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setCursors([]);
  };

  const cursor = cursors[cursors.length - 1];
  const { state, reload } = useAsync(
    () =>
      adminApi.notificationSends({
        address,
        app,
        type,
        status: status || undefined,
        delivery: delivery || undefined,
        category: category || undefined,
        from,
        limit: PAGE,
        cursor,
      }),
    [address, app, type, status, delivery, category, from, cursor],
  );

  if (sendId) {
    return <SendDetail key={sendId} id={sendId} backTo={sendsPath(initialAddress)} />;
  }

  const detailPath = (id: string) => `/notifications/sends/${encodeURIComponent(id)}`;

  return (
    <Screen
      filters={
        <FilterPills
          label="Time window"
          current={windowHours}
          onSelect={(v) => {
            setWindowHours(v);
            setFrom(isoAgo(Number(v)));
            setCursors([]);
          }}
          options={WINDOWS.map((w) => ({ value: w.value as string, label: w.label }))}
        />
      }
      actions={
        <>
          <AddressSearch
            label="Recipient address"
            onSearch={refilter(setAddress)}
          />
          <AnySelect
            label="Status"
            value={status}
            onChange={refilter(setStatus)}
            anyLabel="Any status"
            options={STATUSES.map((v) => ({ value: v, label: statusLabel(v) }))}
          />
          <AnySelect
            label="Delivery"
            value={delivery}
            onChange={refilter(setDelivery)}
            anyLabel="Any delivery"
            options={DELIVERIES.map((v) => ({ value: v, label: deliveryLabel(v) ?? v }))}
          />
          <AnySelect
            label="Category"
            value={category}
            onChange={refilter(setCategory)}
            anyLabel="Any category"
            options={CATEGORIES.map((v) => ({ value: v, label: capitalise(v) }))}
          />
          <Button
            onClick={() => {
              // Re-anchor the window, or "last 24 hours" grows staler per click.
              setFrom(isoAgo(Number(windowHours)));
              reload();
            }}
          >
            Refresh
          </Button>
        </>
      }
      chips={
        address || app || type ? (
          <div style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}>
            {address ? <FilterChip label={`to: ${address}`} onRemove={() => refilter(setAddress)(undefined)} /> : null}
            {app ? <FilterChip label={`app: ${app}`} onRemove={() => refilter(setApp)(undefined)} /> : null}
            {type ? <FilterChip label={`type: ${type}`} onRemove={() => refilter(setType)(undefined)} /> : null}
          </div>
        ) : undefined
      }
    >
      <Tabs tabs={tabs} current="sends" />
      <Panel title="Sends">
        {state.status === 'failed' ? (
          <Failure message={state.message} what="The send log" />
        ) : (
          <AsyncBoundary state={state}>
            {(d) => (
              <>
                {d.items.length === 0 ? (
                  <EmptyState
                    title={cursors.length ? 'No more sends' : 'No sends match'}
                    suggestion="This is the server reporting no rows, not a failed request. Try a wider window, or remove a filter."
                  />
                ) : (
                  <Table columns={['When', 'App', 'Type', 'Category', 'Recipient', 'Status', 'Delivery', '']} actionsLast>
                    {d.items.map((send) => (
                      <tr
                        key={send.id}
                        className="rw-rowhover"
                        onDoubleClick={(ev) => {
                          if ((ev.target as HTMLElement).closest('button')) return;
                          navigate(detailPath(send.id));
                        }}
                      >
                        <Td>
                          <When iso={send.createdAt} />
                        </Td>
                        <Td>
                          <CellFilter active={send.appSlug === app} title={`Filter to ${send.appSlug}`} onClick={() => refilter(setApp)(send.appSlug)}>
                            {send.appSlug}
                          </CellFilter>
                        </Td>
                        <Td>
                          <CellFilter active={send.type === type} title={`Filter to ${send.type}`} onClick={() => refilter(setType)(send.type)}>
                            {send.type}
                          </CellFilter>
                        </Td>
                        <Td>{capitalise(send.category)}</Td>
                        <Td>
                          <CellFilter
                            active={send.recipient === address}
                            title="Filter to this address"
                            onClick={() => refilter(setAddress)(send.recipient)}
                            plain
                          >
                            {send.recipient}
                          </CellFilter>
                        </Td>
                        <Td>
                          <StatusPill status={send.status} reason={send.reason} />
                        </Td>
                        <Td>
                          <DeliveryText status={send.status} delivery={send.deliveryStatus} />
                        </Td>
                        <Td action>
                          <Menu label={`Actions for the ${send.type} to ${send.recipient}`}>
                            {(close) => (
                              <MenuItem href={`#${detailPath(send.id)}`} onClick={close}>
                                Show details
                              </MenuItem>
                            )}
                          </Menu>
                        </Td>
                      </tr>
                    ))}
                  </Table>
                )}
                <Pager
                  page={cursors.length + 1}
                  hasNext={Boolean(d.nextCursor)}
                  onPrevious={() => setCursors(cursors.slice(0, -1))}
                  onNext={() => d.nextCursor && setCursors([...cursors, d.nextCursor])}
                />
              </>
            )}
          </AsyncBoundary>
        )}
      </Panel>
    </Screen>
  );
}

// ── Opt-outs ─────────────────────────────────────────────────────────

const SOURCES: { value: OptOutSource | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'one-click', label: 'One-click' },
  { value: 'email-link', label: 'Email link' },
  { value: 'form', label: 'Form' },
  { value: 'complaint', label: 'Complaint' },
];

/**
 * The opt-out register: who asked not to be emailed, how, and the evidence.
 *
 * The evidence (IP, country, browser) is cleared by auth after 24 months while
 * the opt-out itself stays, so a cleared row says so rather than showing blanks
 * that read as "never collected". A complaint carries none, because it arrives
 * from the provider rather than from the person's browser.
 */
function OptOuts({ tabs }: { tabs: RecordTabs<NotificationSection> }) {
  const [address, setAddress] = useState<string>();
  const [source, setSource] = useState<OptOutSource | ''>('');
  const [app, setApp] = useState<string>();
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];

  const { state, reload } = useAsync(
    () =>
      adminApi.notificationOptOuts({ address, app, source: source || undefined, limit: PAGE, cursor }),
    [address, app, source, cursor],
  );

  const filtered = Boolean(address || app || source);

  return (
    <Screen
      filters={
        <FilterPills
          label="Source"
          current={source}
          onSelect={(v) => {
            setSource(v);
            setCursors([]);
          }}
          options={SOURCES}
        />
      }
      actions={
        <>
          <AddressSearch
            label="Opted-out address"
            onSearch={(v) => {
              setAddress(v);
              setCursors([]);
            }}
          />
          <Button onClick={() => reload()}>Refresh</Button>
        </>
      }
      chips={
        address || app ? (
          <div style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}>
            {address ? <FilterChip label={`address: ${address}`} onRemove={() => { setAddress(undefined); setCursors([]); }} /> : null}
            {app ? <FilterChip label={`app: ${app}`} onRemove={() => { setApp(undefined); setCursors([]); }} /> : null}
          </div>
        ) : undefined
      }
    >
      <Tabs tabs={tabs} current="opt-outs" />
      <Panel
        title="Opt-outs"
        info={
          <InfoBubble label="What is kept, and for how long">
            The opt-out itself is kept for good. The evidence recorded with it (IP address, country
            and browser) is cleared after 24 months.
          </InfoBubble>
        }
      >
        {state.status === 'failed' ? (
          <Failure message={state.message} what="The opt-out list" />
        ) : (
          <AsyncBoundary state={state}>
            {(d) => (
              <>
                {d.items.length === 0 ? (
                  filtered ? (
                    <EmptyState
                      title="No opt-outs match"
                      suggestion="The address search matches whole addresses only. Check the spelling, or remove a filter."
                    />
                  ) : (
                    <EmptyState
                      title="Nobody has opted out"
                      suggestion="That is a normal state. Opt-outs appear here when somebody unsubscribes or reports an email as spam."
                    />
                  )
                ) : (
                  <Table columns={['Address', 'Source', 'App', 'Date', 'Evidence']}>
                    {d.items.map((o) => (
                      <tr key={o.id}>
                        <Td>{o.address}</Td>
                        <Td>{sourceLabel(o.source)}</Td>
                        <Td>
                          {o.appSlug ? (
                            <CellFilter
                              active={o.appSlug === app}
                              title={`Filter to ${o.appSlug}`}
                              onClick={() => {
                                setApp(o.appSlug ?? undefined);
                                setCursors([]);
                              }}
                            >
                              {o.appSlug}
                            </CellFilter>
                          ) : (
                            <span style={{ color: C.muted }}>All apps</span>
                          )}
                        </Td>
                        <Td>
                          <When iso={o.createdAt} />
                        </Td>
                        <Td>
                          <Evidence optOut={o} />
                        </Td>
                      </tr>
                    ))}
                  </Table>
                )}
                <Pager
                  page={cursors.length + 1}
                  hasNext={Boolean(d.nextCursor)}
                  onPrevious={() => setCursors(cursors.slice(0, -1))}
                  onNext={() => d.nextCursor && setCursors([...cursors, d.nextCursor])}
                />
              </>
            )}
          </AsyncBoundary>
        )}
      </Panel>
    </Screen>
  );
}

function Evidence({ optOut }: { optOut: NotificationOptOut }) {
  if (optOut.evidenceClearedAt) {
    return <span style={{ color: C.muted }}>Cleared after 24 months</span>;
  }
  const parts = [optOut.ipAddress, optOut.country].filter((p): p is string => Boolean(p));
  if (parts.length === 0 && !optOut.userAgent) {
    return <span style={{ color: C.muted }}>None collected</span>;
  }
  return (
    <span style={{ display: 'grid', gap: '.1rem' }}>
      {parts.length ? <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '.8rem' }}>{parts.join(', ')}</span> : null}
      {optOut.userAgent ? (
        <span title={optOut.userAgent} style={s.userAgent}>
          {optOut.userAgent}
        </span>
      ) : null}
    </span>
  );
}

// ── Shared controls ──────────────────────────────────────────────────

/**
 * Commits on Enter, like the chip search, because the route matches a WHOLE
 * normalised address: filtering on every keystroke would show "no rows" for
 * every prefix on the way to the one that matches.
 */
function AddressSearch({ label, onSearch }: { label: string; onSearch: (address: string | undefined) => void }) {
  const [draft, setDraft] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = draft.trim();
    onSearch(value || undefined);
    setDraft('');
  };
  return (
    <form onSubmit={submit} style={{ display: 'inline-flex' }}>
      <input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Whole address, then Enter"
        aria-label={label}
        style={{ ...inputStyle, width: 190 }}
      />
    </form>
  );
}

function AnySelect<T extends string>({
  label,
  value,
  onChange,
  anyLabel,
  options,
}: {
  label: string;
  value: T | '';
  onChange: (value: T | '') => void;
  anyLabel: string;
  options: { value: T; label: string }[];
}) {
  return (
    <Select<T | ''>
      label={label}
      value={value}
      onChange={onChange}
      options={[{ value: '', label: anyLabel }, ...options]}
    />
  );
}

function CellFilter({
  children,
  onClick,
  title,
  active,
  plain,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  active: boolean;
  /** Proportional text, for an address rather than a slug. */
  plain?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        ...s.cellFilter,
        ...(plain ? { fontFamily: 'inherit', fontSize: 'inherit' } : {}),
        color: active ? C.brand : C.text,
      }}
    >
      {children}
    </button>
  );
}

function Pager({
  page,
  hasNext,
  onPrevious,
  onNext,
}: {
  page: number;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  if (page === 1 && !hasNext) return null;
  return (
    <div style={s.pager}>
      <span style={{ fontSize: '.82rem', color: C.muted }}>Page {page}, newest first</span>
      <span style={{ display: 'flex', gap: '.4rem', marginLeft: 'auto' }}>
        <Button disabled={page === 1} onClick={onPrevious}>
          Previous
        </Button>
        <Button disabled={!hasNext} onClick={onNext}>
          Next
        </Button>
      </span>
    </div>
  );
}

/** `undefined` for "all time": the route treats an absent `from` as no bound. */
function isoAgo(hours: number): string | undefined {
  if (!hours || hours <= 0) return undefined;
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

function capitalise(word: string): string {
  return word ? word[0]!.toUpperCase() + word.slice(1) : word;
}

const s = {
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: '.75rem',
    padding: '1rem 1.1rem',
  },
  tile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '.15rem',
    padding: '.7rem .85rem .75rem',
    background: C.page,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
  },
  tileLabel: { fontSize: '.75rem', fontWeight: 600, color: C.muted },
  tileValue: { fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.2 },
  tileNote: { fontSize: '.75rem', color: C.warnText },
  cellFilter: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    fontFamily: 'ui-monospace, monospace',
    fontSize: '.8rem',
    textAlign: 'left' as const,
    /* `group-invitation` broke at its hyphen in a narrow pane, which reads as
       two values. The table scrolls sideways instead. */
    whiteSpace: 'nowrap' as const,
    textDecoration: 'underline',
    textDecorationStyle: 'dotted' as const,
    textUnderlineOffset: 3,
  },
  userAgent: {
    display: 'block',
    maxWidth: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '.78rem',
    color: C.muted,
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    gap: '.75rem',
    padding: '.7rem 1.2rem',
    borderTop: `1px solid ${C.border}`,
  },
};
